import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService?.signInWithEmail(formData?.email, formData?.password);
      
      // Check role from both user_metadata AND the profile from public.users table
      const metaRole = result?.user?.user_metadata?.role;
      const profileRole = result?.profile?.role;
      
      if (metaRole !== 'admin' && profileRole !== 'admin') {
        setError('Access denied. Admin credentials required.');
        await authService?.signOut();
        setLoading(false);
        return;
      }

      // Successful admin login
      navigate('/admin-dashboard');
    } catch (err) {
      setError(err?.message || 'Invalid credentials. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService?.resetPassword(resetEmail);
      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(false);
        setResetEmail('');
      }, 3000);
    } catch (err) {
      setError(err?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6 hover:opacity-80 transition-opacity">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
              <Icon name="Shield" size={24} color="white" />
            </div>
            <span className="text-2xl font-heading font-bold text-foreground">
              Studyexpo Admin
            </span>
          </Link>
          
          <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
            Administrative Access
          </h1>
          <p className="text-muted-foreground">
            Secure login for platform administrators
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-border p-8">
          {!showForgotPassword ? (
            <>
              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} color="#dc2626" />
                    <p className="text-sm text-red-600 flex-1">{error}</p>
                  </div>
                )}

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Administrator Email
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Icon name="Mail" size={20} color="#64748b" />
                    </div>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData?.email}
                      onChange={handleChange}
                      placeholder="admin@studyexpo.in"
                      required
                      className="pl-11"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Icon name="Lock" size={20} color="#64748b" />
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData?.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      required
                      className="pl-11 pr-11"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      disabled={loading}
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                      disabled={loading}
                    />
                    <span className="text-sm text-muted-foreground">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                    disabled={loading}
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Login Button */}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Authenticating...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="LogIn" size={20} />
                      <span>Sign In to Admin Panel</span>
                    </>
                  )}
                </Button>
              </form>

              {/* Security Indicators */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Shield" size={16} color="#10b981" />
                    <span>SSL Secured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Lock" size={16} color="#10b981" />
                    <span>Encrypted</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={16} color="#10b981" />
                    <span>Verified</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Forgot Password Form */}
              <div className="mb-6">
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setResetEmail('');
                  }}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                  <Icon name="ArrowLeft" size={16} />
                  <span>Back to login</span>
                </button>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  Reset Password
                </h2>
                <p className="text-sm text-muted-foreground">
                  Enter your admin email to receive password reset instructions
                </p>
              </div>

              {resetSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <Icon name="CheckCircle" size={48} color="#10b981" className="mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    Reset Email Sent
                  </h3>
                  <p className="text-sm text-green-700">
                    Check your inbox for password reset instructions
                  </p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} color="#dc2626" />
                      <p className="text-sm text-red-600 flex-1">{error}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <Icon name="Mail" size={20} color="#64748b" />
                      </div>
                      <Input
                        id="resetEmail"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e?.target?.value)}
                        placeholder="admin@studyexpo.in"
                        required
                        className="pl-11"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Icon name="Loader2" size={20} className="animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={20} />
                        <span>Send Reset Link</span>
                      </>
                    )}
                  </Button>
                </form>
              )}
            </>
          )}
        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Not an admin?{' '}
            <Link to="/institute-login" className="text-primary hover:text-primary/80 font-medium transition-colors">
              Institute Login
            </Link>
            {' '}or{' '}
            <Link to="/student-registration" className="text-primary hover:text-primary/80 font-medium transition-colors">
              Student Portal
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;